import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

interface DashboardStats {
  totalRounds: number;
  averageScore: number;
  handicap: number;
  recentRounds: Array<{
    id: string;
    courseName: string;
    date: string;
    score: number;
    par: number;
  }>;
  favoritesCourses: Array<{
    id: string;
    name: string;
    timesPlayed: number;
    averageScore: number;
  }>;
}

export default function DashboardPage() {
  const [stats, setStats] = useState<DashboardStats>({
    totalRounds: 0,
    averageScore: 0,
    handicap: 0,
    recentRounds: [],
    favoritesCourses: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // TODO: Fetch actual dashboard data from API
    setTimeout(() => {
      setStats({
        totalRounds: 24,
        averageScore: 82.5,
        handicap: 10.2,
        recentRounds: [
          { id: '1', courseName: 'Pine Rivers Golf Club', date: '2024-06-15', score: 82, par: 72 },
          { id: '2', courseName: 'Pine Rivers Golf Club', date: '2024-06-08', score: 85, par: 72 },
          { id: '3', courseName: 'Royal Queensland', date: '2024-05-25', score: 88, par: 72 }
        ],
        favoritesCourses: [
          { id: '1', name: 'Pine Rivers Golf Club', timesPlayed: 15, averageScore: 83.2 },
          { id: '2', name: 'Royal Queensland', timesPlayed: 8, averageScore: 86.5 }
        ]
      });
      setLoading(false);
    }, 1000);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-golf-50 to-emerald-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-golf-500 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-golf-50 to-emerald-50 dark:from-gray-900 dark:to-gray-800">
      <div className="container mx-auto px-4 py-8">
        <div role="note" className="mb-5 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950"><strong>Demo dashboard</strong><span className="ml-2">The rounds, scores and handicap below are sample data. Personal statistics are not connected yet.</span></div>
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-golf-700 to-emerald-600 dark:from-golf-400 dark:to-emerald-300 mb-2">
            Welcome Back, Golfer!
          </h1>
          <p className="text-gray-600 dark:text-gray-400">Here's your golf performance overview</p>
        </div>

        {/* Quick Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800/50 backdrop-blur rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300 hover:bg-gray-50 dark:hover:bg-gray-700/50">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-gradient-to-br from-golf-100 to-emerald-100 dark:from-golf-900 dark:to-emerald-900 p-3 rounded-lg">
                <span className="text-2xl">🏌️</span>
              </div>
              <Link to="/rounds/new" className="text-golf-600 hover:text-golf-700 dark:text-golf-400 dark:hover:text-golf-300 text-sm font-medium">
                New Round →
              </Link>
            </div>
            <h3 className="text-lg font-semibold text-gray-600 dark:text-gray-400">Total Rounds</h3>
            <p className="text-3xl font-bold text-golf-600 dark:text-golf-400">{stats.totalRounds}</p>
          </div>

          <div className="bg-white dark:bg-gray-800/50 backdrop-blur rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300 hover:bg-gray-50 dark:hover:bg-gray-700/50">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900 dark:to-indigo-900 p-3 rounded-lg">
                <span className="text-2xl">📊</span>
              </div>
              <Link to="/stats" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 text-sm font-medium">
                View Stats →
              </Link>
            </div>
            <h3 className="text-lg font-semibold text-gray-600 dark:text-gray-400">Average Score</h3>
            <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">{stats.averageScore.toFixed(1)}</p>
          </div>

          <div className="bg-white dark:bg-gray-800/50 backdrop-blur rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300 hover:bg-gray-50 dark:hover:bg-gray-700/50">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900 dark:to-pink-900 p-3 rounded-lg">
                <span className="text-2xl">🎯</span>
              </div>
              <span className="text-sm text-gray-500 dark:text-gray-400">Index</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-600 dark:text-gray-400">Handicap</h3>
            <p className="text-3xl font-bold text-purple-600 dark:text-purple-400">{stats.handicap.toFixed(1)}</p>
          </div>
        </div>

        {/* Recent Rounds */}
        <div className="grid lg:grid-cols-2 gap-8">
          <div className="bg-white dark:bg-gray-800/50 backdrop-blur rounded-xl shadow-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-800 dark:text-gray-200">Recent Rounds</h2>
              <Link to="/rounds" className="text-golf-600 hover:text-golf-700 dark:text-golf-400 dark:hover:text-golf-300 text-sm font-medium">
                View All →
              </Link>
            </div>
            
            {stats.recentRounds.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 text-center py-8">No rounds recorded yet</p>
            ) : (
              <div className="space-y-3">
                {stats.recentRounds.map(round => (
                  <div key={round.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600/50 transition-colors">
                    <div>
                      <h4 className="font-semibold text-gray-800 dark:text-gray-200">{round.courseName}</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {new Date(round.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-golf-600 dark:text-golf-400">{round.score}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {round.score - round.par > 0 ? '+' : ''}{round.score - round.par}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Favorite Courses */}
          <div className="bg-white dark:bg-gray-800/50 backdrop-blur rounded-xl shadow-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-800 dark:text-gray-200">Favorite Courses</h2>
              <Link to="/courses" className="text-golf-600 hover:text-golf-700 dark:text-golf-400 dark:hover:text-golf-300 text-sm font-medium">
                View All →
              </Link>
            </div>
            
            {stats.favoritesCourses.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 text-center py-8">Play some rounds to see your favorites</p>
            ) : (
              <div className="space-y-3">
                {stats.favoritesCourses.map(course => (
                  <div key={course.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600/50 transition-colors">
                    <div>
                      <h4 className="font-semibold text-gray-800 dark:text-gray-200">{course.name}</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {course.timesPlayed} rounds played
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{course.averageScore.toFixed(1)}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">avg score</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-8 grid md:grid-cols-4 gap-4">
          <Link to="/rounds/new" className="bg-gradient-to-r from-golf-500 to-emerald-500 hover:from-golf-600 hover:to-emerald-600 text-white p-4 rounded-lg text-center font-medium transition-all duration-300 shadow-lg hover:shadow-xl">
            <span className="text-2xl mb-2 block">⛳</span>
            New Round
          </Link>
          <Link to="/courses/new" className="bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white p-4 rounded-lg text-center font-medium transition-all duration-300 shadow-lg hover:shadow-xl">
            <span className="text-2xl mb-2 block">➕</span>
            Add Course
          </Link>
          <Link to="/stats" className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white p-4 rounded-lg text-center font-medium transition-all duration-300 shadow-lg hover:shadow-xl">
            <span className="text-2xl mb-2 block">📈</span>
            View Stats
          </Link>
          <Link to="/rounds" className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white p-4 rounded-lg text-center font-medium transition-all duration-300 shadow-lg hover:shadow-xl">
            <span className="text-2xl mb-2 block">📋</span>
            All Rounds
          </Link>
        </div>
      </div>
    </div>
  );
}
